import type { CareerLayerApiResponse } from '@/domains/careerLayer/careerLayer.entity';
import type { DayName } from '@/domains/shared';
import type { ConsolidateCareerLayerByYearsUseCase } from './consolidateCareerLayerByYearsUseCase';
import type { FilterCareerLayerByJornadaUseCase } from './filterCareerLayerByJornadaUseCase';
import type { ProcessedCareerLayerData } from './filterCareerLayerByJornadaUseCase';
import type { FilterCareerLayerByYearUseCase } from './filterCareerLayerByYearUseCase';

/**
 * UseCase Maestro que orquesta el procesamiento completo de CareerLayer
 *
 * Flujo:
 * 1. Filtrar años (excluir futuros)
 * 2. Consolidar todos los años en un objeto por jornada
 * 3. Filtrar por jornada específica (si aplica)
 * 4. Retornar data procesada lista para UI
 *
 * Este UseCase coordina los 3 UseCases anteriores
 */
export class ProcessCareerLayerDataUseCase {
  constructor(
    private filterByYearUseCase: FilterCareerLayerByYearUseCase,
    private consolidateByYearsUseCase: ConsolidateCareerLayerByYearsUseCase,
    private filterByJornadaUseCase: FilterCareerLayerByJornadaUseCase,
  ) {}

  /**
   * Ejecuta el procesamiento completo en cadena
   * @param apiData - Respuesta cruda de la API
   * @param jornadaFilter - Filtro opcional de jornada
   * @returns Data procesada lista para mostrar en UI
   */
  execute(
    apiData: CareerLayerApiResponse | null,
    jornadaFilter: DayName | null,
  ): ProcessedCareerLayerData {
    // 1. Filtrar años <= año actual
    const filteredByYear = this.filterByYearUseCase.execute(apiData);

    // 2. Consolidar todos los años en { D: {...}, V: {...} }
    const consolidatedByYears =
      this.consolidateByYearsUseCase.execute(filteredByYear);

    // 3. Filtrar por jornada (o consolidar ambas si no hay filtro)
    const processed = this.filterByJornadaUseCase.execute(
      consolidatedByYears,
      jornadaFilter,
    );

    return processed;
  }
}
